import {
  domainViewSchema,
  operationViewSchema,
  siteBackupViewSchema,
  siteDatabaseCredentialsSchema,
  siteDatabaseViewSchema,
  siteSftpCredentialsSchema,
  siteSftpViewSchema,
  siteViewSchema,
  type DomainView,
  type OperationView,
  type SiteBackupView,
  type SiteDatabaseCredentials,
  type SiteDatabaseView,
  type SiteSftpCredentials,
  type SiteSftpView,
  type SiteView,
} from '@kraaapf/contracts';
import { z } from 'zod';

import type { PortalClient } from '../http-client';

/** Ein bestellbarer Website-Typ. */
const blueprintSchema = z.object({
  key: z.string(),
  version: z.string(),
  displayName: z.string(),
  minResources: z.object({
    cpuMilli: z.number(),
    memoryMb: z.number(),
    diskGb: z.number(),
  }),
});

export type Blueprint = z.infer<typeof blueprintSchema>;

/** Die Antwort auf eine Bestellung: der Soll-Zustand und der Auftrag dazu. */
const createSiteResponseSchema = z.object({
  site: siteViewSchema,
  operationId: z.string(),
});

/**
 * Websites und alles, was an ihnen hängt.
 *
 * Die Aufrufe geben durchweg den **Soll**-Zustand zurück und nicht den
 * beobachteten: `stop()` liefert eine Site, deren `desiredState` `stopped`
 * ist — ob auf dem Worker etwas angehalten wurde, weiß erst das nächste
 * Inventar. Die Oberfläche zeigt deshalb beide Zustände nebeneinander und
 * nicht einen davon als „den" Zustand.
 */
function buildSitesApi(client: PortalClient) {
  return {
    async blueprints(): Promise<Blueprint[]> {
      return client.get('/sites/blueprints', z.array(blueprintSchema));
    },

    async list(): Promise<SiteView[]> {
      return client.get('/sites', z.array(siteViewSchema));
    },

    async get(siteId: string): Promise<SiteView> {
      return client.get(`/sites/${siteId}`, siteViewSchema);
    },

    async operations(siteId: string, limit = 50): Promise<OperationView[]> {
      return client.get(`/sites/${siteId}/operations`, z.array(operationViewSchema), {
        query: { limit },
      });
    },

    async create(input: {
      blueprintKey: string;
      region?: string;
      labels?: Record<string, string>;
      diskGb?: number;
    }) {
      return client.post('/sites', createSiteResponseSchema, input);
    },

    async stop(siteId: string): Promise<SiteView> {
      return client.post(`/sites/${siteId}/stop`, siteViewSchema);
    },

    async start(siteId: string): Promise<SiteView> {
      return client.post(`/sites/${siteId}/start`, siteViewSchema);
    },

    /** Nimmt eine aufgegebene Website wieder in den Abgleich auf. */
    async retry(siteId: string): Promise<SiteView> {
      return client.post(`/sites/${siteId}/retry`, siteViewSchema);
    },

    /**
     * Hebt die Website auf den aktuellen Stand ihres Blueprints.
     *
     * Aktualisiert **die Anwendung nicht**: Bei WordPress liegen Kern,
     * Erweiterungen und Themes im Datenverzeichnis und werden von WordPress
     * selbst gepflegt. Das Image bestimmt PHP- und Apache-Stand.
     */
    async upgrade(siteId: string): Promise<SiteView> {
      return client.post(`/sites/${siteId}/upgrade`, siteViewSchema);
    },

    /**
     * Merkt eine Website zum Löschen vor.
     *
     * Antwortet mit der Site im Soll-Zustand `deleted` — sie ist damit noch da.
     * Weg ist sie, wenn der Worker es gemeldet hat und die Reservierung
     * freigegeben wurde.
     */
    async remove(siteId: string): Promise<SiteView> {
      return client.del(`/sites/${siteId}`, siteViewSchema);
    },

    // -- Domains -------------------------------------------------------------

    async domains(siteId: string): Promise<DomainView[]> {
      return client.get(`/sites/${siteId}/domains`, z.array(domainViewSchema));
    },

    async addDomain(siteId: string, fqdn: string): Promise<DomainView[]> {
      return client.post(`/sites/${siteId}/domains`, z.array(domainViewSchema), { fqdn });
    },

    async removeDomain(siteId: string, domainId: string): Promise<void> {
      await client.send('DELETE', `/sites/${siteId}/domains/${domainId}`);
    },

    // -- Datenbanken ---------------------------------------------------------

    async databases(siteId: string): Promise<SiteDatabaseView[]> {
      return client.get(`/sites/${siteId}/databases`, z.array(siteDatabaseViewSchema));
    },

    /** Die Zugangsdaten stehen **genau einmal** in dieser Antwort. */
    async createDatabase(siteId: string): Promise<SiteDatabaseCredentials> {
      return client.post(`/sites/${siteId}/databases`, siteDatabaseCredentialsSchema);
    },

    async rotateDatabasePassword(
      siteId: string,
      databaseId: string,
    ): Promise<SiteDatabaseCredentials> {
      return client.post(
        `/sites/${siteId}/databases/${databaseId}/rotate-password`,
        siteDatabaseCredentialsSchema,
      );
    },

    async removeDatabase(siteId: string, databaseId: string): Promise<void> {
      await client.send('DELETE', `/sites/${siteId}/databases/${databaseId}`);
    },

    // -- SFTP ----------------------------------------------------------------

    async sftpAccounts(siteId: string): Promise<SiteSftpView[]> {
      return client.get(`/sites/${siteId}/sftp`, z.array(siteSftpViewSchema));
    },

    async createSftpAccount(
      siteId: string,
      publicKeys: string[] = [],
    ): Promise<SiteSftpCredentials> {
      return client.post(`/sites/${siteId}/sftp`, siteSftpCredentialsSchema, { publicKeys });
    },

    async rotateSftpPassword(siteId: string, accountId: string): Promise<SiteSftpCredentials> {
      return client.post(
        `/sites/${siteId}/sftp/${accountId}/rotate-password`,
        siteSftpCredentialsSchema,
      );
    },

    /** Ersetzt die hinterlegten Schlüssel vollständig — die Liste ist der Stand. */
    async replaceSftpKeys(
      siteId: string,
      accountId: string,
      publicKeys: string[],
    ): Promise<SiteSftpView> {
      return client.patch(`/sites/${siteId}/sftp/${accountId}`, siteSftpViewSchema, {
        publicKeys,
      });
    },

    async removeSftpAccount(siteId: string, accountId: string): Promise<void> {
      await client.send('DELETE', `/sites/${siteId}/sftp/${accountId}`);
    },

    // -- Sicherung -----------------------------------------------------------

    async backups(siteId: string): Promise<SiteBackupView> {
      return client.get(`/sites/${siteId}/backups`, siteBackupViewSchema);
    },

    async requestBackup(siteId: string): Promise<void> {
      await client.send('POST', `/sites/${siteId}/backups`);
    },

    /**
     * Holt eine Momentaufnahme zurück.
     *
     * Der Agent hält den Stack an, legt den **jetzigen** Stand unter
     * `deleted/` ab, stellt her und startet wieder. Der beiseitegelegte Stand
     * bleibt erhalten — das ist der Unterschied zwischen einem Rückweg und
     * einem Überschreiben, und er gehört in jeden Text, der diesen Aufruf
     * auslöst.
     */
    async restore(siteId: string, snapshotId: string): Promise<void> {
      await client.send('POST', `/sites/${siteId}/restore`, { snapshotId });
    },
  };
}

/** Die öffentliche Form dieses Bereichs — abgeleitet, damit sie nicht zweimal dasteht. */
export type SitesApi = ReturnType<typeof buildSitesApi>;

export function createSitesApi(client: PortalClient): SitesApi {
  return buildSitesApi(client);
}
